import { Fragment, h } from '@atomify/jsx';
import {
    ExtendedApartmentBuilding,
    ExtendedConstructionNumber,
    ExtendedProjectPhase,
    ExtendedPropertyType,
} from '@bpd-library/utilities';
import { getCurrentView, ViewCardObject, ViewCardTypes } from '@features/properties/store';
import { ProjectPhaseCard } from '@molecules/project-phase-card/project-phase-card';
import { PropertyCard } from '@molecules/property-card/property-card';
import LazyRender from '@source/components/atoms/lazy-render/lazy-render';
import {
    formatApartmentBuildingStatus,
    formatConstructionNumberStatus,
    formatDetailPageUrl,
    formatLivingArea,
    formatPrice,
    formatProjectPhaseStatus,
    formatPropertyTypeStatus,
    formatRooms,
    formatShoppingCartUrl,
    FormattedSpecification,
} from '@source/utilities/api-formatters';
import { getDebugState } from '@store/debugger';
import { isConstructionNumberNhg } from '@utilities/api-utilities/is-nhg';
import { isMobile } from '@utilities/media-query';

const apartmentBuildingTile = (apartmentBuilding: ExtendedApartmentBuilding) => {
    return (
        <LazyRender
            classes="properties-view__property-type"
            threshold={0.1}
            renderFunction={() => {
                const { image, displayName, projectPhaseName, id } = apartmentBuilding;

                return (
                    <PropertyCard
                        title={displayName || ''}
                        project={projectPhaseName}
                        image={image?.url || ''}
                        status={formatApartmentBuildingStatus(apartmentBuilding)}
                        price={formatPrice({ apartmentBuilding })}
                        debugId={getDebugState().propertiesId ? id : ''}
                        orientation="horizontal"
                    />
                );
            }}
        />
    );
};

const constructionNumberTile = (constructionNumber: ExtendedConstructionNumber) => {
    return (
        <LazyRender
            classes="properties-view__construction-number"
            renderFunction={() => {
                const {
                    id,
                    propertyTypeName,
                    mainImage,
                    identifier,
                    wishlistItemCount,
                    propertyType,
                    areaProject,
                } = constructionNumber;

                const specs: FormattedSpecification[] = [];

                const rooms = formatRooms(constructionNumber);
                const livingArea = formatLivingArea(constructionNumber);

                if (rooms) specs.push(rooms);
                if (livingArea) specs.push(livingArea);

                return (
                    <bpd-construction-number-card
                        classes=""
                        href={formatDetailPageUrl({ constructionNumber })}
                        id={id}
                        house-name={`${propertyTypeName} #${identifier}`}
                        price={formatPrice({ constructionNumber })}
                        status={formatConstructionNumberStatus(constructionNumber)}
                        image={{
                            url: mainImage?.url || '',
                            alt: mainImage?.title || '',
                        }}
                        isNhg={isConstructionNumberNhg(constructionNumber)}
                        specs={specs}
                        wishlistItemCount={wishlistItemCount}
                        shoppingCartUrl={formatShoppingCartUrl(constructionNumber)}
                        hidePrice={!propertyType?.canShowPrice}
                        debugId={getDebugState().propertiesId ? id : ''}
                        compare={!areaProject?.website?.disablePropertyComparison}
                    />
                );
            }}
        />
    );
};

const propertyTypeTile = (propertyType: ExtendedPropertyType) => {
    return (
        <LazyRender
            classes="properties-view__property-type"
            threshold={0.1}
            renderFunction={() => {
                const { mainImage, name, id, projectPhaseName, canShowPrice } = propertyType;

                return (
                    <PropertyCard
                        id={id}
                        title={name || ''}
                        project={projectPhaseName}
                        href={formatDetailPageUrl({ propertyType })}
                        image={mainImage?.url || ''}
                        price={formatPrice({ propertyType })}
                        status={formatPropertyTypeStatus(propertyType)}
                        hidePrice={!canShowPrice}
                        debugId={getDebugState().propertiesId ? id : ''}
                        orientation="horizontal"
                    />
                );
            }}
        />
    );
};

const projectPhaseTile = (projectPhase: ExtendedProjectPhase) => {
    return (
        <LazyRender
            classes="properties-view__property-type"
            threshold={0.1}
            renderFunction={() => {
                const { mainImage, name, id, constructionNumbers, status } = projectPhase;

                const specs: FormattedSpecification[] = [];
                const livingArea = formatLivingArea(projectPhase);
                if (livingArea) specs.push(livingArea);

                return (
                    <ProjectPhaseCard
                        id={id}
                        title={name || ''}
                        image={mainImage?.url || ''}
                        housesAmount={constructionNumbers?.length}
                        price={status !== 'Sold' ? formatPrice({ projectPhase }) : undefined}
                        status={formatProjectPhaseStatus(projectPhase)}
                        debugId={getDebugState().propertiesId ? id : ''}
                        specs={specs}
                    />
                );
            }}
        />
    );
};

const handleInteraction = (item: ViewCardObject, cardType: ViewCardTypes, cb?: Function) => {
    return (e: MouseEvent) => {
        cb && cb(e, item, cardType);
    };
};

const getTiles = (onMouseenter: Function, onMouseleave: Function): HTMLElement | undefined => {
    const currentView = getCurrentView();
    const data = currentView?.data;

    if (!data) return;

    const ButtonContainer = ({
        item,
        card,
        cardType,
        attr,
    }: {
        item: ViewCardObject;
        card: Element;
        cardType: ViewCardTypes;
        attr?: {};
    }) => (
        <button
            class="properties-view__tile"
            id={`container-${item.id}`}
            onClick={handleInteraction(item, cardType, currentView?.onCardClick)}
            onMouseenter={handleInteraction(item, cardType, onMouseenter)}
            onMouseleave={handleInteraction(item, cardType, onMouseleave)}
            onTouchStart={!isMobile() ? handleInteraction(item, cardType, onMouseenter) : null}
            onTouchEnd={!isMobile() ? handleInteraction(item, cardType, onMouseleave) : null}
            {...attr}>
            {card}
        </button>
    );

    return (
        <Fragment>
            <div></div>
            {data.map((item) => {
                switch (item.objectType) {
                    case 'projectPhase': {
                        return (
                            item.mainImage?.url && (
                                <ButtonContainer
                                    item={item}
                                    cardType="projectPhase"
                                    card={projectPhaseTile(item)}
                                />
                            )
                        );
                    }
                    case 'apartmentBuilding': {
                        return (
                            item.image?.url && (
                                <ButtonContainer
                                    item={item}
                                    cardType="apartmentBuilding"
                                    card={apartmentBuildingTile(item)}
                                />
                            )
                        );
                    }
                    case 'propertyType': {
                        return (
                            item.mainImage?.url && (
                                <ButtonContainer
                                    item={item}
                                    cardType="propertyType"
                                    card={propertyTypeTile(item)}
                                    attr={{
                                        ['data-track-property-type-id']: item.id,
                                        ...(item.status && {
                                            ['data-track-property-type-status']: item.status,
                                        }),
                                    }}
                                />
                            )
                        );
                    }
                    case 'constructionNumber': {
                        return (
                            item.mainImage?.url && (
                                <ButtonContainer
                                    item={item}
                                    cardType="constructionNumber"
                                    card={constructionNumberTile(item)}
                                    attr={{
                                        ['data-track-construction-number-id']: item.id,
                                        ...(item.status && {
                                            ['data-track-construction-number-status']: item.status,
                                        }),
                                        ...(item.propertyType && {
                                            ['data-track-property-type-id']: item.propertyType.id,
                                        }),
                                        ...(item.propertyType?.status && {
                                            ['data-track-property-type-status']:
                                                item.propertyType.status,
                                        }),
                                    }}
                                />
                            )
                        );
                    }
                }
            })}
        </Fragment>
    );
};

export { getTiles };
